import {
  EJobPostingStatus,
  JOB_STATUS_OPTIONS,
  JOB_TYPE_OPTIONS,
  thunkActionLoading,
} from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, Skeleton } from 'antd'
import { t } from 'i18next'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { careerLandingManagementAPI } from 'src/api/career-landing-management'
import { Button, Input } from 'src/common'
import { ShareTextEditor } from 'src/components/shared'
import { createJobAction } from 'src/redux/actions/career-landing-management'
import { defaultValues, IJobSchema, jobPostSchema } from './schema'

type IProps = {
  onCancel: () => void
  title?: string
  jobId?: string
}

export const AddNewForm = ({ onCancel, title, jobId }: IProps) => {
  const dispatch = useAppDispatch()
  const [isGettingDetail, setIsGettingDetail] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { isSubmitted },
  } = useForm<IJobSchema>({
    resolver: zodResolver(jobPostSchema),
    defaultValues: defaultValues,
  })

  const isAdding = useAppSelector(
    (state) => state.careerLanding.loadings[thunkActionLoading.CREATE_JOB]
  )

  const handleDataEditor = (data: string, key: keyof typeof defaultValues) => {
    if (data === '<p></p>') {
      setValue(key, '')
      if (isSubmitted) {
        trigger(key)
      }
      return
    }
    setValue(key, data)
    if (isSubmitted) {
      trigger(key)
    }
  }

  const onSubmit = async (data: IJobSchema) => {
    try {
      if (jobId) {
        setIsUpdating(true)
        const res = await careerLandingManagementAPI.updateJobPosting(jobId, {
          ...data,
          status: Number(data.status),
        })
        setIsUpdating(false)
        if (res.success) {
          onCancel()
          message.success({
            content: res?.message ?? t('common:save_success'),
          })
        }
      } else {
        const res = await dispatch(
          createJobAction({ ...data, status: Number(data.status) })
        ).unwrap()

        if (res.success) {
          onCancel()
          message.success({
            content: res?.message ?? t('common:save_success'),
          })
        }
      }
    } catch (error: any) {
      setIsUpdating(false)
      message.error({
        content:
          error?.message ?? t('error:some_thing_went_wrong_please_try_again'),
      })
    }
  }
  const getDetailJob = async () => {
    try {
      if (jobId) {
        setIsGettingDetail(true)
        const res = await careerLandingManagementAPI.getDetailJobId(jobId)
        if (res.data.id) {
          setValue('title', res?.data?.title || '')
          setValue('titleInDutch', res?.data?.titleInDutch || '')
          setValue('address', res?.data?.address || '')
          setValue(
            'dateExpired',
            moment(res?.data?.dateExpired).format('YYYY-MM-DD') || ''
          )
          setValue('type', res?.data?.type ?? '')
          setValue('status', res?.data?.status ?? undefined)
          setValue('description', res?.data?.description || '')
          setValue('descriptionInDutch', res?.data?.descriptionInDutch || '')
          setValue('keyResponsibilities', res?.data?.keyResponsibilities || '')
          setValue(
            'keyResponsibilitiesInDutch',
            res?.data?.keyResponsibilitiesInDutch || ''
          )
          setValue('requirements', res?.data?.requirements || '')
          setValue('requirementsInDutch', res?.data?.requirementsInDutch || '')
          setValue('whatWeOffer', res?.data?.whatWeOffer || '')
          setValue('whatWeOfferInDutch', res?.data?.whatWeOfferInDutch || '')
        }
        setIsGettingDetail(false)
      }
    } catch (error: any) {
      message.error({
        content:
          error?.message ?? t('error:some_thing_went_wrong_please_try_again'),
      })
      setIsGettingDetail(false)
    }
  }

  useEffect(() => {
    getDetailJob()
  }, [jobId])

  const dateExpired = watch('dateExpired')

  useEffect(() => {
    if (moment(dateExpired).isAfter())
      setValue('status', EJobPostingStatus.PUBLISHED)
  }, [dateExpired])

  const listStatusOptions = useMemo(() => {
    if (moment(dateExpired).isAfter()) {
      return JOB_STATUS_OPTIONS.map((item) => ({
        ...item,
        disabled: item.value === EJobPostingStatus.EXPIRED ? true : false,
      }))
    }
    return JOB_STATUS_OPTIONS
  }, [dateExpired])
  useEffect(() => {
    if (moment(dateExpired).isBefore()) {
      setValue('status', EJobPostingStatus.EXPIRED)
    }
  }, [dateExpired])

  return (
    <div data-aos="fade-right">
      <h1 className="text-xl mb-5">
        {title || t('evolveLanding:add_new_job')}
      </h1>
      {isGettingDetail ? (
        <Skeleton paragraph={{ rows: 10 }} />
      ) : (
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          <Controller
            control={control}
            name="title"
            render={({ field, fieldState: { error } }) => (
              <Input
                className="!h-[38px]"
                alignment="col"
                required
                label={t('evolveLanding:job_title')}
                placeholder={t('evolveLanding:enter_job_title')}
                errors={error?.message}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="titleInDutch"
            render={({ field, fieldState: { error } }) => (
              <Input
                className="!h-[38px]"
                alignment="col"
                required
                label={t('evolveLanding:job_title_in_NL')}
                placeholder={t('evolveLanding:enter_job_title_in_NL')}
                errors={error?.message}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="address"
            render={({ field, fieldState: { error } }) => (
              <Input
                className="!h-[38px]"
                alignment="col"
                required
                label={t('evolveLanding:address')}
                placeholder={t('evolveLanding:enter_address')}
                errors={error?.message}
                {...field}
              />
            )}
          />

          <div className="grid grid-cols-3 gap-x-4">
            <Controller
              control={control}
              name="dateExpired"
              render={({ field, fieldState: { error } }) => (
                <Input
                  alignment="col"
                  type="date"
                  required
                  label={t('evolveLanding:date_expired')}
                  placeholder={t('evolveLanding:job_title')}
                  errors={error?.message}
                  value={field.value}
                  onChangeDate={(data) => field.onChange(data)}
                />
              )}
            />
            <Controller
              control={control}
              name="type"
              render={({ field, fieldState: { error } }) => (
                <Input
                  alignment="col"
                  type="select"
                  required
                  options={JOB_TYPE_OPTIONS}
                  label={t('evolveLanding:type')}
                  className="!h-[38px]"
                  style={{ marginTop: 0 }}
                  placeholder={t('evolveLanding:select_status')}
                  errors={error?.message}
                  value={field.value}
                  onChangeSelect={field.onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="status"
              render={({ field, fieldState: { error } }) => (
                <Input
                  alignment="col"
                  type="select"
                  required
                  options={listStatusOptions}
                  label={t('evolveLanding:status')}
                  disabled={moment(dateExpired).isBefore()}
                  className="!h-[38px]"
                  style={{ marginTop: 0 }}
                  placeholder={t('evolveLanding:select_type')}
                  errors={error?.message}
                  value={field.value}
                  onChangeSelect={field.onChange}
                />
              )}
            />
          </div>

          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName={'!border-slate-200 !rounded-t-md'}
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:description')}
                required
                placeholder={t('evolveLanding:insert_text')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) => handleDataEditor(data, 'description')}
              />
            )}
          />

          <Controller
            control={control}
            name="descriptionInDutch"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:description_in_NL')}
                required
                placeholder={t('evolveLanding:insert_text')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) =>
                  handleDataEditor(data, 'descriptionInDutch')
                }
              />
            )}
          />

          <Controller
            control={control}
            name="keyResponsibilities"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:key_responsibility')}
                required
                placeholder={t('evolveLanding:this_role')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) =>
                  handleDataEditor(data, 'keyResponsibilities')
                }
              />
            )}
          />

          <Controller
            control={control}
            name="keyResponsibilitiesInDutch"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:key_responsibility_in_NL')}
                required
                placeholder={t('evolveLanding:this_role')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) =>
                  handleDataEditor(data, 'keyResponsibilitiesInDutch')
                }
              />
            )}
          />
          <Controller
            control={control}
            name="requirements"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:requirements')}
                required
                placeholder={t('evolveLanding:this_role')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) => handleDataEditor(data, 'requirements')}
              />
            )}
          />

          <Controller
            control={control}
            name="requirementsInDutch"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:requirements_in_NL')}
                required
                placeholder={t('evolveLanding:this_role')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) =>
                  handleDataEditor(data, 'requirementsInDutch')
                }
              />
            )}
          />

          <Controller
            control={control}
            name="whatWeOffer"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:what_we_offer')}
                required
                placeholder={t('evolveLanding:this_role')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) => handleDataEditor(data, 'whatWeOffer')}
              />
            )}
          />
          <Controller
            control={control}
            name="whatWeOfferInDutch"
            render={({ field, fieldState: { error } }) => (
              <ShareTextEditor
                toolbarClassName="!border-slate-200 !rounded-t-md"
                className="!border-slate-200 rounded-b-md"
                label={t('evolveLanding:what_we_offer_in_NL')}
                required
                placeholder={t('evolveLanding:this_role')}
                errors={error?.message}
                value={field.value as any}
                onChange={(data: any) =>
                  handleDataEditor(data, 'whatWeOfferInDutch')
                }
              />
            )}
          />
        </div>
      )}
      <div className="flex w-full justify-end gap-x-4 mt-5">
        <Button
          onClick={() => {
            onCancel()
            document.body.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        >
          Cancel
        </Button>
        <Button
          loading={isAdding || isUpdating}
          type="primary"
          onClick={handleSubmit(async (data) => {
            await onSubmit(data)
            document.body.scrollTo({ top: 0, behavior: 'smooth' })
          })}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
